import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  ChatCircleText, Users, FileText, ClockCounterClockwise,
  ChartBar, PaperPlaneTilt, SignOut, House, WhatsappLogo
} from "@phosphor-icons/react";
import { Button } from "./ui/button";
import { Toaster } from "./ui/sonner";
import PreviewBanner from "./PreviewBanner";

const links = [
  { to: "/dashboard", label: "Overview", icon: House, testid: "nav-dashboard" },
  { to: "/inbox", label: "Inbox", icon: ChatCircleText, testid: "nav-inbox" },
  { to: "/clients", label: "Clients", icon: Users, testid: "nav-clients" },
  { to: "/documents", label: "Documents", icon: FileText, testid: "nav-documents" },
  { to: "/flash-reports", label: "Flash Reports", icon: PaperPlaneTilt, testid: "nav-flash" },
  { to: "/whatsapp", label: "WhatsApp", icon: WhatsappLogo, testid: "nav-whatsapp" },
  { to: "/activity", label: "Activity Log", icon: ClockCounterClockwise, testid: "nav-activity" },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#F9FAFB] text-slate-900">
      <aside className="fixed top-0 left-0 h-screen w-64 bg-white border-r border-slate-200 flex flex-col z-30">
        <div className="px-5 py-5 border-b border-slate-200 flex items-center gap-3">
          <div className="h-9 w-9 rounded-md bg-[#0F172A] grid place-items-center text-white">
            <FileText size={18} weight="duotone" />
          </div>
          <div>
            <div className="font-[Manrope] font-extrabold tracking-tight text-slate-900">Doctrack</div>
            <div className="text-[10px] uppercase font-mono text-slate-500 tracking-widest">Comm · Hub</div>
          </div>
        </div>
        <nav className="flex-1 py-4 px-3 space-y-1">
          {links.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-150 ${
                  isActive ? "bg-[#0F172A] text-white" : "text-slate-600 hover:bg-slate-100"
                }`
              }
            >
              <Icon size={18} weight="regular" />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-slate-200 p-3">
          <div className="flex items-center gap-3 px-2 py-2">
            {user?.picture ? (
              <img src={user.picture} alt="" className="h-8 w-8 rounded-full" />
            ) : (
              <div className="h-8 w-8 rounded-full bg-slate-200 grid place-items-center text-xs font-bold">
                {(user?.name || "U")[0]}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold truncate">{user?.name}</div>
              <div className="text-[11px] text-slate-500 truncate font-mono">{user?.email}</div>
            </div>
          </div>
          <Button
            variant="ghost"
            data-testid="logout-btn"
            onClick={logout}
            className="w-full justify-start text-slate-600 hover:text-slate-900"
          >
            <SignOut size={16} className="mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      <main className="pl-64">
        <div className="max-w-[1400px] mx-auto p-8">
          <PreviewBanner />
          <Outlet />
        </div>
      </main>
      <Toaster position="top-right" richColors />
    </div>
  );
}
